type MarketingBannerConfigurationModalSkeletonProps = {
  isMobile: boolean;
  showCampaign?: boolean;
};

const SkeletonBox = ({
  width,
  height,
  radius = 4,
  className = "",
}: {
  width?: string | number;
  height: number;
  radius?: number;
  className?: string;
}) => (
  <div
    className={`animate-pulse bg-surface-tonal-default ${className}`}
    style={{
      width: width ?? "100%",
      height,
      borderRadius: radius,
      flexShrink: 0,
    }}
  />
);

const SegmentControlSkeleton = () => (
  <div className="flex gap-2 rounded-md bg-surface-secondary p-1">
    <SkeletonBox height={32} radius={6} className="flex-1" />
    <SkeletonBox height={32} radius={6} className="flex-1" />
  </div>
);

const FieldSkeleton = ({ testId }: { testId?: string } = {}) => (
  <div className="flex flex-col gap-2" data-testid={testId}>
    <SkeletonBox width={88} height={16} radius={4} />
    <SkeletonBox width={220} height={12} radius={4} />
    <SkeletonBox height={40} radius={8} />
  </div>
);

const SizeCardsSkeleton = () => (
  <div className="flex flex-wrap gap-3 pt-1">
    {Array.from({ length: 4 }, (_, index) => (
      <SkeletonBox
        key={`marketing-banner-size-skeleton-${index}`}
        width={71}
        height={56}
        radius={8}
      />
    ))}
  </div>
);

const FormSkeleton = ({ showCampaign = false }: { showCampaign?: boolean }) => (
  <div className="flex flex-col gap-6">
    <SegmentControlSkeleton />
    <div className="flex flex-col gap-2">
      <SkeletonBox width={88} height={16} radius={4} />
      <SkeletonBox width={260} height={12} radius={4} />
      <SizeCardsSkeleton />
    </div>
    <FieldSkeleton />
    <FieldSkeleton />
    {showCampaign && <FieldSkeleton testId="campaign-field-skeleton" />}
    <FieldSkeleton />
  </div>
);

const PreviewSkeleton = () => (
  <div className="flex flex-col items-center gap-3">
    <SkeletonBox width={240} height={160} radius={8} />
  </div>
);

export const MarketingBannerConfigurationModalSkeleton = ({
  isMobile,
  showCampaign,
}: MarketingBannerConfigurationModalSkeletonProps) => {
  if (isMobile) {
    return (
      <div
        data-testid="marketing-banner-configuration-modal-skeleton"
        className="flex flex-col gap-6"
      >
        <div className="relative flex min-h-[200px] items-center justify-center overflow-hidden rounded-2xl bg-surface-secondary p-4">
          <PreviewSkeleton />
          <SkeletonBox
            width={32}
            height={32}
            radius={8}
            className="absolute right-2 top-2"
          />
        </div>
        <FormSkeleton showCampaign={showCampaign} />
      </div>
    );
  }

  return (
    <div data-testid="marketing-banner-configuration-modal-skeleton">
      <div className="flex min-h-[560px]">
        <div className="flex flex-1 items-center justify-center bg-surface-secondary p-8">
          <PreviewSkeleton />
        </div>

        <div className="flex flex-1 flex-col gap-6 p-8">
          <div className="flex flex-col gap-2">
            <div className="flex justify-end">
              <SkeletonBox width={24} height={24} radius={6} />
            </div>
            <SkeletonBox width={220} height={28} radius={6} />
          </div>

          <div className="-mx-2 flex min-h-0 flex-1 flex-col overflow-y-auto px-2 py-1">
            <FormSkeleton showCampaign={showCampaign} />
          </div>

          <div className="flex justify-end">
            <SkeletonBox width={112} height={40} radius={8} />
          </div>
        </div>
      </div>
    </div>
  );
};
